import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiDelete, apiGet, ApiError } from '../api/client';
import type { HistoryDeleteResponse, HistoryEntry, HistoryListResponse } from '../api/types';
import { formatDate, formatDuration, formatTimestamp, truncate } from '../lib/format';
import Icon from '../components/Icon';
import TopAppBar, { AccountActionButton } from '../components/TopAppBar';

/**
 * Four Obsidian filter buckets shown as pill chips. The server's
 * `GET /history?kind=` only accepts a single kind, so buckets that map to
 * several kinds (Voice = voice-input/quick-dictation/ai-preview, etc.) are
 * applied client-side via `familyOf` — the `kind` query param is no longer
 * sent. `q`, `from`, `to`, `cursor`, `limit` still go to the server.
 */
type Bucket = 'all' | 'voice' | 'ai' | 'compose';

const BUCKETS: { id: Bucket; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'voice', label: 'Voice' },
  { id: 'ai', label: 'AI' },
  { id: 'compose', label: 'Compose' },
];

type Family = 'voice' | 'ai' | 'compose' | 'other';

/** Map a server kind string into one of the four bucket families. */
function familyOf(kind: string): Family {
  const k = kind.toLowerCase();
  if (
    k === 'voice-input' ||
    k === 'quick-dictation' ||
    k === 'ai-preview' ||
    k.includes('voice') ||
    k.includes('dictat') ||
    k === 'meeting' ||
    k === 'note'
  ) {
    return 'voice';
  }
  if (k === 'rewrite' || k === 'proofread' || k === 'style') return 'ai';
  if (
    k === 'compose' ||
    k === 'summarise' ||
    k === 'bullet_points' ||
    k.includes('summar') ||
    k.includes('bullet') ||
    k.includes('paraphrase')
  ) {
    return 'compose';
  }
  return 'other';
}

/** Human label for the kind badge in the detail header. */
function familyLabel(kind: string): string {
  const fam = familyOf(kind);
  if (fam === 'voice') return 'Voice';
  if (fam === 'ai') return 'AI';
  if (fam === 'compose') return 'Compose';
  return kind.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Convert a yyyy-mm-dd input to unix milliseconds (start/end of day). */
function toDateMillis(value: string, endOfDay: boolean): number | null {
  if (!value) return null;
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return endOfDay ? new Date(`${value}T23:59:59.999`).getTime() : d.getTime();
}

/** Normalize a Unix-seconds-or-ms timestamp to milliseconds. */
function toMillis(ts: number): number {
  return ts < 1e12 ? ts * 1000 : ts;
}

/** Time-only label, e.g. "10:42 AM". */
function formatTime(ts: number): string {
  if (!Number.isFinite(ts)) return '—';
  return new Date(toMillis(ts)).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Word count for AI/compose entries (the "245 words" meta). */
function wordCount(text: string): number {
  const t = text.trim();
  if (t.length === 0) return 0;
  return t.split(/\s+/).length;
}

/** Day bucket label for section headers: Today / Yesterday / Last Week / date. */
function dayBucket(ts: number): string {
  const now = new Date();
  const ms = toMillis(ts);
  const d = new Date(ms);
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfYesterday = startOfToday - 86_400_000;
  const startOfLastWeek = startOfToday - 7 * 86_400_000;
  if (d.getTime() >= startOfToday) return 'Today';
  if (d.getTime() >= startOfYesterday) return 'Yesterday';
  if (d.getTime() >= startOfLastWeek) return 'Last Week';
  return formatDate(ts);
}

type LoadState = 'idle' | 'loading' | 'error' | 'ready';

/**
 * History view — two-pane Obsidian layout: a 400px grouped list on the left
 * (filter chips, debounced search, date-range filters) and a read-only
 * detail panel on the right. Backed by `GET /history?cursor&limit&q&from&to`
 * with client-side bucket filtering, and `DELETE /history` for Clear All.
 */
export default function HistoryView() {
  const [entries, setEntries] = useState<HistoryEntry[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [state, setState] = useState<LoadState>('idle');
  const [error, setError] = useState<string | null>(null);

  // Filter inputs.
  const [qInput, setQInput] = useState('');
  const [bucket, setBucket] = useState<Bucket>('all');
  const [from, setFrom] = useState(''); // yyyy-mm-dd
  const [to, setTo] = useState('');

  // Debounced query actually sent to the server.
  const [q, setQ] = useState('');
  const [retryNonce, setRetryNonce] = useState(0);
  const debounceRef = useRef<number | undefined>(undefined);

  // Monotonic request id. Incremented on every reload and every loadMore;
  // a response only commits state if its id is still current. This prevents
  // an orphaned loadMore response from appending stale entries to a new
  // filter's list when the user changes filters before the fetch resolves.
  const requestIdRef = useRef(0);

  // Selected entry for the read-only detail pane.
  const [selected, setSelected] = useState<HistoryEntry | null>(null);

  // Clear-all (DELETE /history) state.
  const [clearing, setClearing] = useState(false);
  const [clearError, setClearError] = useState<string | null>(null);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setQ(qInput.trim()), 300);
    return () => {
      clearTimeout(debounceRef.current);
    };
  }, [qInput]);

  const buildPath = useCallback(
    (cursor: string | null): string => {
      const params = new URLSearchParams();
      params.set('limit', '50');
      if (q) params.set('q', q);
      const fromTs = toDateMillis(from, false);
      if (fromTs !== null) params.set('from', String(fromTs));
      const toTs = toDateMillis(to, true);
      if (toTs !== null) params.set('to', String(toTs));
      if (cursor) params.set('cursor', cursor);
      return `/history?${params.toString()}`;
    },
    [q, from, to],
  );

  // Reload from page 1 whenever the server-side filter set changes.
  // Bucket changes do NOT refetch (filtering is client-side).
  useEffect(() => {
    const reqId = ++requestIdRef.current;
    setState('loading');
    setError(null);
    setSelected(null);
    apiGet<HistoryListResponse>(buildPath(null))
      .then((res) => {
        if (reqId !== requestIdRef.current) return;
        setEntries(res.entries);
        setNextCursor(res.nextCursor);
        setState('ready');
      })
      .catch((err: unknown) => {
        if (reqId !== requestIdRef.current) return;
        setError(err instanceof ApiError ? err.message : 'Failed to load history');
        setState('error');
      });
    return () => {
      // Invalidate any in-flight reload on cleanup.
      requestIdRef.current++;
    };
  }, [buildPath, retryNonce]);

  const loadMore = useCallback(() => {
    if (!nextCursor) return;
    const reqId = ++requestIdRef.current;
    setState('loading');
    apiGet<HistoryListResponse>(buildPath(nextCursor))
      .then((res) => {
        if (reqId !== requestIdRef.current) return;
        setEntries((prev) => [...prev, ...res.entries]);
        setNextCursor(res.nextCursor);
        setState('ready');
      })
      .catch((err: unknown) => {
        if (reqId !== requestIdRef.current) return;
        setError(err instanceof ApiError ? err.message : 'Failed to load more');
        setState('error');
      });
  }, [buildPath, nextCursor]);

  /** DELETE /history — clears all cloud history for the user. */
  const requestClear = useCallback(() => {
    setClearing(true);
    setClearError(null);
    apiDelete<HistoryDeleteResponse>('/history')
      .then(() => {
        setClearing(false);
        setEntries([]);
        setNextCursor(null);
        setSelected(null);
        setState('ready');
      })
      .catch((err: unknown) => {
        setClearing(false);
        setClearError(err instanceof ApiError ? err.message : 'Failed to clear history');
      });
  }, []);

  const clearAll = useCallback(() => {
    if (!window.confirm('Delete all cloud history? This cannot be undone.')) return;
    requestClear();
  }, [requestClear]);

  // Client-side bucket filtering + day grouping.
  const filtered = useMemo(
    () => (bucket === 'all' ? entries : entries.filter((e) => familyOf(e.kind) === bucket)),
    [entries, bucket],
  );

  const groups = useMemo(() => {
    const out: { label: string; entries: HistoryEntry[] }[] = [];
    const indexByLabel = new Map<string, number>();
    for (const entry of filtered) {
      const label = dayBucket(entry.created_at);
      const idx = indexByLabel.get(label);
      if (idx === undefined) {
        indexByLabel.set(label, out.length);
        out.push({ label, entries: [entry] });
      } else {
        out[idx]!.entries.push(entry);
      }
    }
    return out;
  }, [filtered]);

  const appBarActions = (
    <>
      <button
        type="button"
        onClick={clearAll}
        disabled={clearing}
        title="Clear all history"
        className="flex h-10 items-center gap-2 rounded-lg px-3 py-1.5 font-label-md text-label-md text-error transition-colors hover:bg-error-container/20 disabled:opacity-50"
      >
        <Icon name="delete" className="text-[18px]" />
        <span>{clearing ? 'Clearing…' : 'Clear All'}</span>
      </button>
      <AccountActionButton />
    </>
  );

  return (
    <>
      <TopAppBar title="History" actions={appBarActions} />

      {/* Two-pane layout. mt-16 clears the fixed 64px app bar; height fills
          the viewport minus the app bar (desktop) and bottom nav (mobile). */}
      <div className="mx-auto mt-16 flex h-[calc(100dvh-8rem-env(safe-area-inset-bottom))] w-full max-w-max-width-content overflow-hidden md:h-[calc(100dvh-4rem)]">
        {/* Left: list */}
        <div className="flex h-full w-full flex-col border-r border-outline-variant/20 bg-surface-container-low md:w-[400px] md:flex-shrink-0">
          {/* Filters */}
          <div className="shrink-0 space-y-3 border-b border-outline-variant/20 bg-surface-container-low p-gutter">
            <div className="flex gap-2 overflow-x-auto no-scrollbar">
              {BUCKETS.map((b) => {
                const active = bucket === b.id;
                return (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => setBucket(b.id)}
                    className={[
                      'min-h-11 whitespace-nowrap rounded-full px-4 py-1.5 font-label-sm text-label-sm transition-colors',
                      active
                        ? 'bg-primary-container text-on-primary-container'
                        : 'bg-surface-container text-on-surface-variant hover:bg-surface-container-high',
                    ].join(' ')}
                  >
                    {b.label}
                  </button>
                );
              })}
            </div>

            {/* Search */}
            <div className="relative">
              <Icon
                name="search"
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant"
              />
              <input
                type="search"
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                aria-label="Search transcripts"
                placeholder="Search transcripts…"
                className="h-[52px] w-full rounded-xl bg-surface-container-high pl-12 pr-4 font-body-md text-body-md text-on-surface placeholder:text-on-surface-variant focus:outline-none focus:ring-2 focus:ring-primary/60"
              />
            </div>

            {/* Date range */}
            <div className="flex gap-2">
              <label className="flex min-w-0 flex-1 items-center gap-2 rounded-xl bg-surface-container-high px-3 h-[52px] focus-within:ring-2 focus-within:ring-primary/60">
                <span className="font-label-sm text-label-sm text-on-surface-variant">From</span>
                <input
                  type="date"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  className="w-full bg-transparent font-body-sm text-body-sm text-on-surface [color-scheme:dark] focus:outline-none"
                />
              </label>
              <label className="flex min-w-0 flex-1 items-center gap-2 rounded-xl bg-surface-container-high px-3 h-[52px] focus-within:ring-2 focus-within:ring-primary/60">
                <span className="font-label-sm text-label-sm text-on-surface-variant">To</span>
                <input
                  type="date"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  className="w-full bg-transparent font-body-sm text-body-sm text-on-surface [color-scheme:dark] focus:outline-none"
                />
              </label>
            </div>
          </div>

          {/* List */}
          <div className="flex-1 space-y-6 overflow-y-auto bg-surface-container-lowest p-gutter">
            {clearError && (
              <div className="flex items-center justify-between gap-3 rounded-xl bg-error-container/20 px-4 py-3 font-body-sm text-body-sm text-error" role="alert">
                <span className="min-w-0 break-words">{clearError}</span>
                <button
                  type="button"
                  onClick={requestClear}
                  disabled={clearing}
                  className="min-h-11 shrink-0 rounded-lg px-3 font-label-sm text-label-sm text-error transition-colors hover:bg-error-container/30 disabled:opacity-50"
                >
                  Try again
                </button>
              </div>
            )}

            {state === 'error' && (
              <div className="flex items-center justify-between gap-3 rounded-xl bg-error-container/20 px-4 py-3 font-body-sm text-body-sm text-error" role="alert">
                <span className="min-w-0 break-words">{error ?? 'Failed to load history'}</span>
                <button
                  type="button"
                  onClick={() => setRetryNonce((value) => value + 1)}
                  className="min-h-11 shrink-0 rounded-lg px-3 font-label-sm text-label-sm text-error transition-colors hover:bg-error-container/30"
                >
                  Retry
                </button>
              </div>
            )}

            {state === 'loading' && entries.length === 0 && <ListSkeleton />}

            {state !== 'error' && state !== 'loading' && filtered.length === 0 && (
              <EmptyState hasEntries={entries.length > 0} />
            )}

            {groups.map((group) => (
              <section key={group.label}>
                <h3 className="mb-3 font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant">
                  {group.label}
                </h3>
                <div className="divide-y divide-outline-variant/20">
                  {group.entries.map((entry) => {
                    const active = selected?.entry_id === entry.entry_id;
                    const fam = familyOf(entry.kind);
                    const isVoice = fam === 'voice';
                    const title = truncate(entry.output_text || entry.original_text || '(empty)', 80);
                    const meta =
                      isVoice
                        ? formatDuration(entry.duration_ms)
                        : `${wordCount(entry.output_text || entry.original_text)} words`;
                    return (
                      <button
                        key={entry.entry_id}
                        type="button"
                        onClick={() => setSelected(entry)}
                        className={[
                          'relative flex min-h-16 items-start gap-3 px-3 py-3 text-left transition-colors',
                          active
                            ? 'bg-surface-container-high before:absolute before:inset-y-2 before:left-0 before:w-0.5 before:bg-primary'
                            : 'group hover:bg-surface-container',
                        ].join(' ')}
                      >
                        <span
                          className={[
                            'mt-1 flex shrink-0 items-center justify-center rounded-lg p-2',
                            isVoice
                              ? 'bg-primary/20 text-primary'
                              : 'bg-surface-variant text-secondary group-hover:bg-secondary/10',
                          ].join(' ')}
                        >
                          <Icon name={isVoice ? 'mic' : 'magic_button'} fill={isVoice} className="text-[20px]" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-title-md text-title-md text-on-surface">
                            {title}
                          </span>
                          <span className="mt-1 flex items-center gap-2 font-body-sm text-body-sm text-on-surface-variant">
                            <span>{formatTime(entry.created_at)}</span>
                            <span className="h-1 w-1 rounded-full bg-outline-variant" />
                            <span>{meta}</span>
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>
            ))}

            {nextCursor && (
              <div className="flex justify-center pt-2">
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={state === 'loading'}
                  className="min-h-11 rounded-lg bg-surface-container px-4 py-2 font-label-lg text-label-lg text-on-surface transition-colors hover:bg-surface-container-high disabled:opacity-50"
                >
                  {state === 'loading' ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right: detail panel (desktop) */}
        <div className="hidden h-full flex-1 flex-col overflow-hidden bg-surface-container-lowest md:flex">
          {selected ? (
            <DetailPanel
              entry={selected}
              onClear={clearAll}
              clearing={clearing}
            />
          ) : (
            <DetailEmpty />
          )}
        </div>
      </div>

      {/* Mobile detail overlay */}
      {selected && (
        <div className="fixed inset-0 z-[60] flex flex-col bg-surface-container-lowest md:hidden">
          <DetailPanel
            entry={selected}
            onClose={() => setSelected(null)}
            onClear={clearAll}
            clearing={clearing}
          />
        </div>
      )}
    </>
  );
}

/** Loading skeleton for the initial list fetch. */
function ListSkeleton() {
  return (
    <div className="space-y-6">
      {[0, 1, 2].map((g) => (
        <div key={g} className="space-y-2">
          <div className="mb-3 h-3 w-20 animate-pulse rounded bg-surface-container" />
          {[0, 1].map((i) => (
            <div
              key={i}
              className="flex items-start gap-3 rounded-xl border border-transparent bg-surface-container-low p-3"
            >
              <div className="mt-1 h-9 w-9 animate-pulse rounded-lg bg-surface-container" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-full animate-pulse rounded bg-surface-container" />
                <div className="h-3 w-24 animate-pulse rounded bg-surface-container" />
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/** Empty state — no history at all, or no matches for the current filters. */
function EmptyState({ hasEntries }: { hasEntries: boolean }) {
  if (hasEntries) {
    return (
      <p className="py-12 text-center font-body-md text-body-md text-on-surface-variant">
        No conversations match these filters.
      </p>
    );
  }
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-surface-container text-on-surface-variant">
        <Icon name="mic" className="text-[28px]" />
      </span>
      <div>
        <p className="font-title-md text-title-md text-on-surface">No history yet</p>
        <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant">
          Your dictations and AI conversations will appear here.
        </p>
      </div>
    </div>
  );
}

/** Right-pane placeholder when nothing is selected (desktop). */
function DetailEmpty() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 px-gutter text-center">
      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-surface-container text-on-surface-variant">
        <Icon name="history" className="text-[32px]" />
      </span>
      <div>
        <p className="font-title-md text-title-md text-on-surface">Select an entry</p>
        <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant">
          Choose a conversation from the list to read it here.
        </p>
      </div>
    </div>
  );
}

/**
 * Read-only detail panel — kind badge, timestamp + duration meta, title,
 * copy + delete actions, and a scrollable content card showing the output
 * (and the original when it differs). No edit per the Phase 105 contract.
 * The delete action calls DELETE /history (the only available delete) which
 * clears all cloud history; it is shared with the app-bar Clear All button.
 */
function DetailPanel({
  entry,
  onClose,
  onClear,
  clearing,
}: {
  entry: HistoryEntry;
  onClose?: () => void;
  onClear: () => void;
  clearing: boolean;
}) {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(() => {
    const text = entry.output_text || entry.original_text || '';
    if (text.length === 0) return;
    navigator.clipboard
      ?.writeText(text)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {
        /* clipboard unavailable — silently ignore */
      });
  }, [entry.output_text, entry.original_text]);

  const fam = familyOf(entry.kind);
  const isVoice = fam === 'voice';
  const title = truncate(entry.output_text || entry.original_text || '(empty)', 120);
  const showOriginal =
    entry.original_text.length > 0 && entry.original_text !== entry.output_text;
  const meta =
    isVoice
      ? `${formatTimestamp(entry.created_at)} • ${formatDuration(entry.duration_ms)} duration`
      : `${formatTimestamp(entry.created_at)} • ${wordCount(entry.output_text)} words`;

  return (
    <>
      {/* Header */}
      <div className="relative z-10 shrink-0 border-b border-outline-variant/20 bg-surface-container-lowest p-6 pb-6 md:p-8">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="mb-2 flex items-center gap-3">
              <span className="flex items-center gap-1 rounded bg-primary/10 px-2 py-1 font-label-sm text-label-sm uppercase tracking-wider text-primary">
                <Icon name={isVoice ? 'mic' : 'magic_button'} fill={isVoice} className="text-[14px]" />
                {familyLabel(entry.kind)}
              </span>
              <span className="font-body-sm text-body-sm text-on-surface-variant">{meta}</span>
            </div>
            <h2 className="font-display-md text-display-md font-bold leading-tight text-on-surface">
              {title}
            </h2>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                title="Back"
                className="flex min-h-11 min-w-11 items-center justify-center rounded-lg text-on-surface-variant transition-colors hover:bg-surface-container hover:text-on-surface md:hidden"
              >
                <Icon name="arrow_back" />
              </button>
            )}
            <button
              type="button"
              onClick={copy}
              title="Copy"
              className="flex min-h-11 min-w-11 items-center justify-center rounded-lg text-on-surface-variant transition-colors hover:bg-surface-container hover:text-on-surface"
            >
              <Icon name={copied ? 'check' : 'content_copy'} />
            </button>
            <button
              type="button"
              onClick={onClear}
              disabled={clearing}
              title="Delete all history"
              className="flex min-h-11 min-w-11 items-center justify-center rounded-lg text-on-surface-variant transition-colors hover:bg-error-container/20 hover:text-error disabled:opacity-50"
            >
              <Icon name="delete" />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative flex-1 overflow-y-auto p-6 md:p-8">
        <div className="mx-auto max-w-3xl">
          <div className="rounded-2xl bg-surface-container p-6 md:p-8">
            {showOriginal && (
              <section className="mb-6 border-b border-outline-variant/30 pb-6">
                <h3 className="mb-2 font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant">
                  Original
                </h3>
                <p className="whitespace-pre-wrap font-body-md text-body-md text-on-surface/80">
                  {entry.original_text}
                </p>
              </section>
            )}
            <section>
              {showOriginal && (
                <h3 className="mb-2 font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant">
                  Output
                </h3>
              )}
              <p className="whitespace-pre-wrap font-body-md text-body-md text-on-surface/90">
                {entry.output_text || '(empty)'}
              </p>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}
